import {useMutation, useQueryClient} from '@tanstack/react-query';
import {v4 as uuid} from 'uuid';
import CustomerPortalAPI, {DeepThoughtError, Policy, Requirement} from '../../../APIs/CustomerPortalAPI';
import {queryKeyFn as policiesQueryKeyFn} from './usePoliciesForCustomerQuery';

type RequirementMutationInput = Partial<Requirement>;

async function createRequirementForCustomer(
    ...args: Parameters<typeof CustomerPortalAPI.createRequirementForCustomer>
) {
    const resp = await CustomerPortalAPI.createRequirementForCustomer(...args);
    if (resp?.errors || !resp?.data?.createRequirement) throw new DeepThoughtError(resp?.errors);
    return resp?.data.createRequirement;
}

async function updateRequirementForCustomer(
    ...args: Parameters<typeof CustomerPortalAPI.updateRequirementForCustomer>
) {
    const resp = await CustomerPortalAPI.updateRequirementForCustomer(...args);
    if (resp?.errors || !resp?.data?.updateRequirement) throw new DeepThoughtError(resp?.errors);
    return resp?.data.updateRequirement;
}

// eslint-disable-next-line import/prefer-default-export
export function useRequirementMutation() {
    const queryClient = useQueryClient();

    return useMutation<Requirement, Error, RequirementMutationInput, {previousPolicies: Policy[]; tempId: string}>(
        async (input) => {
            /* prettier-ignore */
            const mutate = input.id
                ? updateRequirementForCustomer
                : createRequirementForCustomer;
            return mutate(input);
        },
        {
            onMutate: (input) => {
                const queryKey = policiesQueryKeyFn(input.customerId);
                queryClient.cancelQueries({queryKey});

                const previousPolicies = queryClient.getQueryData<Policy[]>(queryKey) || [];
                const tempId = uuid();
                queryClient.setQueriesData({queryKey}, (old: Policy[] = []) => {
                    if (input.policyId) {
                        const oldIndex = old.findIndex((p) => p.id === input.policyId);
                        if (oldIndex !== -1) {
                            const oldValue = old[oldIndex];
                            return [
                                ...old.slice(0, oldIndex),
                                {
                                    ...oldValue,
                                    requirements: [...oldValue.requirements, {...input, tempId} as Requirement],
                                },
                                ...old.slice(oldIndex + 1),
                            ];
                        }
                    }
                    return [...old];
                });

                return {previousPolicies, tempId};
            },
            onError: (err, input, context) => {
                if (context?.previousPolicies) {
                    queryClient.setQueryData(policiesQueryKeyFn(input.customerId), context.previousPolicies);
                }
            },
            onSettled: (data, error, input, ctx) => {
                const queryKey = policiesQueryKeyFn(input.customerId);
                queryClient.cancelQueries({queryKey});

                if (data && ctx?.tempId) {
                    const queryKey = policiesQueryKeyFn(input.customerId);
                    queryClient.setQueriesData({queryKey}, (old: Policy[] = []) => {
                        const oldIndex = old.findIndex((p) => p.id === input.policyId);
                        if (oldIndex !== -1) {
                            const oldValue = old[oldIndex];
                            const oldRequirementIndex = oldValue.requirements.findIndex(
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                (r) => (r as any).tempId === ctx.tempId,
                            );
                            const requirements = [
                                ...oldValue.requirements.slice(0, oldRequirementIndex),
                                data,
                                ...oldValue.requirements.slice(oldRequirementIndex + 1),
                            ];
                            return [...old.slice(0, oldIndex), {...oldValue, requirements}, ...old.slice(oldIndex + 1)];
                        }

                        return [...old];
                    });
                } else {
                    queryClient.invalidateQueries({queryKey: policiesQueryKeyFn(input.customerId)});
                }
            },
        },
    );
}
